import React, { useState, useEffect, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Phone, PhoneOff, Mic, MicOff, Clock, X } from 'lucide-react';
import { Device, Call } from '@twilio/voice-sdk';

interface BrowserCallComposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiUrl: string;
  user: any;
  selectedUserPhone?: string;
}

interface CallState {
  status: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';
  duration: number;
  isMuted: boolean;
}

const BrowserCallComposeModal: React.FC<BrowserCallComposeModalProps> = ({ 
  isOpen, 
  onClose, 
  apiUrl, 
  user, 
  selectedUserPhone 
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callState, setCallState] = useState<CallState>({
    status: 'idle',
    duration: 0,
    isMuted: false
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pendingCallParams = useRef<{ to: string, from: string } | null>(null);
  const callInitiatedRef = useRef<boolean>(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhoneNumber('');
      setError('');
      setCallState({
        status: 'idle',
        duration: 0,
        isMuted: false
      });
      callInitiatedRef.current = false;
    }
  }, [isOpen]);

  // Initialize Twilio Voice SDK for browser calls
  useEffect(() => {
    if (!isOpen) return;

    // Add global error handler for unhandled errors
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);
    };

    window.addEventListener('error', handleGlobalError);

    // Cleanup function
    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, [isOpen]);

  // Initialize Twilio Device for browser calls
  const initializeDevice = async (token: string) => {
    try {
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }

      deviceRef.current = new Device(token);
      
      // Register the device
      await deviceRef.current.register();
      
      // Try immediate connection as fallback
      setTimeout(() => {
        if (pendingCallParams.current) {
          initiateCallDirectly();
        }
      }, 1000); // 1 second delay to allow registration
    } catch (error: any) {
      console.error('Error initializing Twilio Device:', error);
      throw error;
    }

    deviceRef.current.on('registered', async () => {
      if (pendingCallParams.current && !callInitiatedRef.current) {
        callInitiatedRef.current = true;
        try {
          const { to, from } = pendingCallParams.current;
          setCallState(prev => ({ ...prev, status: 'connecting' }));
          const call = await deviceRef.current!.connect({ params: { To: to, From: from } });
          callRef.current = call;
          pendingCallParams.current = null;
          
          // Set up call event handlers
          setupCallEventHandlers(call);
        } catch (error: any) {
          console.error('Error initiating call:', error);
          setError(`Call error: ${error.message || 'Failed to connect'}`);
          setCallState(prev => ({ ...prev, status: 'error' }));
          callInitiatedRef.current = false;
        }
      }
    });

    // Also listen for 'ready' event as fallback
    deviceRef.current.on('ready', async () => {
      if (pendingCallParams.current && !callInitiatedRef.current) {
        callInitiatedRef.current = true;
        try {
          const { to, from } = pendingCallParams.current;
          setCallState(prev => ({ ...prev, status: 'connecting' }));
          const call = await deviceRef.current!.connect({ params: { To: to, From: from } });
          callRef.current = call;
          pendingCallParams.current = null;
          
          // Set up call event handlers
          setupCallEventHandlers(call);
        } catch (error: any) {
          console.error('Error initiating call:', error);
          setError(`Call error: ${error.message || 'Failed to connect'}`);
          setCallState(prev => ({ ...prev, status: 'error' }));
          callInitiatedRef.current = false;
        }
      }
    });
    
    deviceRef.current.on('error', (error: any) => {
      console.error('Twilio Device error:', error);
      
      // Check for specific error types
      if (error.code === 31007 && error.message.includes('Client version not supported')) {
        setError(`SDK Version Error: Please refresh the page to load the latest Twilio SDK version`);
      } else if (error.message && error.message.includes('JWT')) {
        setError(`JWT Error: ${error.message}`);
      } else if (error.message && error.message.includes('token')) {
        setError(`Token Error: ${error.message}`);
      } else {
        setError(`Device error: ${error.message || 'Unknown device error'}`);
      }
      setCallState(prev => ({ ...prev, status: 'error' }));
    });
    
    deviceRef.current.on('incoming', (_call: Call) => {
      // Handle incoming calls if needed
    });
    
    deviceRef.current.on('disconnect', () => {
      setCallState(prev => ({ ...prev, status: 'disconnected' }));
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      setTimeout(() => {
        onClose();
      }, 2000);
    });
  };

  // Set up call event handlers
  const setupCallEventHandlers = (call: Call) => {
    call.on('accept', () => {
      setCallState(prev => ({ ...prev, status: 'connected' }));
      
      const startTime = Date.now();
      durationIntervalRef.current = setInterval(() => {
        const duration = Math.floor((Date.now() - startTime) / 1000);
        setCallState(prev => ({ ...prev, duration }));
      }, 1000);
      
      // Don't auto-close the modal - let the user control the call
    });

    call.on('disconnect', () => {
      setCallState(prev => ({ ...prev, status: 'disconnected' }));
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      setTimeout(() => {
        onClose();
      }, 2000);
    });

    call.on('error', (callError: any) => {
      console.error('Call error:', callError);
      setError(`Call error: ${callError.message || 'Unknown error occurred'}`);
      setCallState(prev => ({ ...prev, status: 'error' }));
      setTimeout(() => {
        onClose();
      }, 3000);
    });
  };

  // Direct call initiation (fallback)
  const initiateCallDirectly = async () => {
    if (!pendingCallParams.current || !deviceRef.current || callInitiatedRef.current) return;
    
    callInitiatedRef.current = true;
    try {
      const { to, from } = pendingCallParams.current;
      setCallState(prev => ({ ...prev, status: 'connecting' }));
      const call = await deviceRef.current.connect({ params: { To: to, From: from } });
      callRef.current = call;
      pendingCallParams.current = null;
      
      // Set up call event handlers
      setupCallEventHandlers(call);
    } catch (error: any) {
      console.error('Error in direct call initiation:', error);
      setError(`Call error: ${error.message || 'Failed to connect'}`);
      setCallState(prev => ({ ...prev, status: 'error' }));
      callInitiatedRef.current = false;
    }
  };

  // Normalize phone number to E.164 format
  const normalizePhoneNumber = (input: string) => {
    const digits = (input || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
    return input;
  };

  // Browser call functions
  const initiateBrowserCall = async () => {
    if (!apiUrl || !user || !phoneNumber.trim()) {
      setError('Please enter a phone number to call');
      return;
    }

    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    if (!normalizedPhone.startsWith('+')) {
      setError('Please enter a valid phone number (e.g., +1234567890)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let jwtToken: string | undefined;
      try {
        const session = await fetchAuthSession();
        jwtToken = session.tokens?.idToken?.toString();
        
        if (!jwtToken) {
          throw new Error('No authentication token available');
        }
      } catch (error) {
        console.error('Authentication error:', error);
        throw new Error('Authentication failed');
      }
      
      const response = await fetch(`${apiUrl}/app/voice/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          to: normalizedPhone,
          fromPhoneNumber: selectedUserPhone
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Voice token error response:', errorData);
        throw new Error(errorData.error || 'Failed to generate voice token');
      }

      const { token, toPhoneNumber, fromPhoneNumber } = await response.json();

      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        throw new Error('Calls require HTTPS. Please use a secure connection.');
      }

      pendingCallParams.current = { to: toPhoneNumber || normalizedPhone, from: fromPhoneNumber };
      await initializeDevice(token);
    } catch (err: any) {
      console.error('Browser call initiation error:', err);
      setError(err.message || 'Failed to generate voice token');
      setCallState(prev => ({ ...prev, status: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const endBrowserCall = () => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
    if (deviceRef.current) {
      deviceRef.current.destroy();
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    setCallState({
      status: 'idle',
      duration: 0,
      isMuted: false
    });
    setError('');
  };

  const toggleMute = () => {
    if (callRef.current) {
      if (callState.isMuted) {
        callRef.current.mute(false);
      } else {
        callRef.current.mute(true);
      }
      setCallState(prev => ({ ...prev, isMuted: !prev.isMuted }));
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount or close
  useEffect(() => {
    return () => {
      if (callRef.current) {
        callRef.current.disconnect();
        callRef.current = null;
      }
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="rounded-lg border bg-card text-card-foreground shadow-lg w-full max-w-md">
        <div className="flex flex-col space-y-1.5 p-4 sm:p-6 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold leading-none tracking-tight flex items-center space-x-2">
              <Phone className="h-5 w-5" />
              <span>Call</span>
            </h3>
            <button
              onClick={onClose}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Phone Number Input - only show when not in call */}
          {callState.status === 'idle' && (
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Phone Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1234567890"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loading && phoneNumber.trim()) {
                    initiateBrowserCall();
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                Enter the phone number you want to call. Press Enter to start the call.
              </p>
            </div>
          )}

          {/* Call Status Display */}
          {callState.status !== 'idle' && (
            <div className="text-center">
              <div className="text-lg font-semibold mb-2">
                {callState.status === 'connecting' && 'Connecting...'}
                {callState.status === 'connected' && 'Connected'}
                {callState.status === 'disconnected' && 'Call ended'}
                {callState.status === 'error' && 'Call failed'}
              </div>
              
              {callState.status === 'connected' && (
                <div className="flex items-center justify-center mb-4 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4 mr-1" />
                  {formatDuration(callState.duration)}
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Call Controls - Only show during active call */}
          {callState.status === 'connected' && (
            <div className="flex justify-center space-x-4">
              <button
                onClick={toggleMute}
                className={`rounded-full p-4 ${
                  callState.isMuted 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                {callState.isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
              </button>

              <button
                onClick={endBrowserCall}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-4"
              >
                <PhoneOff className="h-6 w-6" />
              </button>
            </div>
          )}

          {(callState.status === 'disconnected' || callState.status === 'error') && (
            <div className="flex justify-center">
              <button
                onClick={onClose}
                className="bg-gray-500 hover:bg-gray-600 text-white rounded-full p-4"
              >
                Close
              </button>
            </div>
          )}

          {/* Instructions */}
          <div className="text-xs text-muted-foreground text-center">
            {callState.status === 'idle' && (
              <div>
                <p>Enter a phone number and click Call to start</p>
                <p className="mt-1">This will connect directly through your browser</p>
              </div>
            )}
            {callState.status === 'connecting' && (
              <p>Establishing connection...</p>
            )}
            {callState.status === 'connected' && (
              <p>Call in progress - use the controls above to manage the call</p>
            )}
            {callState.status === 'disconnected' && (
              <p>Call ended - modal will close automatically</p>
            )}
            {callState.status === 'error' && (
              <p>Call failed - modal will close automatically</p>
            )}
          </div>

          {/* Action Buttons */}
          {callState.status === 'idle' && (
            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
              >
                Cancel
              </button>
              <button
                onClick={initiateBrowserCall}
                disabled={loading || !phoneNumber.trim()}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Phone className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Connecting...' : 'Call'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowserCallComposeModal;
